import React from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Avatar from '../Avatar'
import Icon from '../icon/Icon'
import Text from '../Text'
import StatusTag from '../tag/StatusTag'
import { RowAligned } from '../layout/LayoutAtoms'
import ExternIcon from '../icon/ExternIcon'
import CircleIcon from '../icon/CircleIcon'
import JobTag from '../tag/JobTag'
import Button from '../Button'
import { NewDot } from '../SecondaryNavigation'

// Styles
import { mqFrom, mqTo } from '../../styles/mediaQueries'
import { shadows, instantTransition } from '../../styles/effects'

// Utils
import { useAppState, ITheme } from '../../context/AppContext'
import { calculateDaysLeft, formatDate } from '../../utils/dateHelper'

// Types
import { ICompanyMember } from '../../common/models/Company'
import { IconType } from '../icon/IconType'
import {
  IFeedback,
  FeedbackStatus as FeedbackState,
} from '../../common/models/Feedback'
import { SingleEvaluationStatus } from '../../common/models/Evaluation'

type CtaType = 'show' | 'continue' | 'start' | 'remove' | 'refresh'

type FeedbackType = 'incoming' | 'outgoing'

export type FeedbackStatus =
  | 'new'
  | 'not_started'
  | 'in_progress'
  | 'done'
  | 'unfinished'

export interface ICta {
  type: CtaType
  disabled?: boolean
  onClick: () => void
}

interface IProps {
  type: FeedbackType
  avatars: Array<ICompanyMember | 'external' | IconType>
  name: string
  date: string
  status?: FeedbackStatus | SingleEvaluationStatus
  cta?: ICta
  secondCta?: ICta
  overdue?: boolean
  bigAvatars?: boolean
  className?: string
  elements?: React.ReactNode
  // A small red dot next to the name — an unopened notification about this
  // feedback exists.
  isNew?: boolean
}

export const getAttributes = (type: FeedbackType, feedback: IFeedback) => {
  const isExternal = feedback.externalFeedbacker
  const dateRequested = feedback.dateCreated && new Date(feedback.dateCreated)

  const feedbackDeadline = dateRequested
    ? dateRequested.setDate(dateRequested.getDate() + 14)
    : 0

  const deadline = calculateDaysLeft(feedbackDeadline)
  return {
    name:
      type === 'incoming'
        ? isExternal
          ? feedback.externalFeedbacker?.fullName || ''
          : feedback.sender?.name || ''
        : feedback.member?.name || '',
    date: formatDate(feedback.dateCreated),
    status:
      type === 'outgoing' && feedback.status === FeedbackState.NOT_STARTED
        ? 'new'
        : type === 'outgoing' && feedback.status === FeedbackState.IN_PROGRESS
        ? 'unfinished'
        : (feedback.status as FeedbackStatus),
    overdue:
      type === 'outgoing' &&
      (feedback.status === FeedbackState.IN_PROGRESS ||
        feedback.status === FeedbackState.NOT_STARTED) &&
      deadline < 0,
  }
}

const PeopleRow: React.FC<IProps> = ({
  cta,
  secondCta,
  avatars,
  name,
  date,
  status,
  overdue,
  type,
  bigAvatars,
  className,
  elements,
  isNew,
}) => {
  const { theme } = useAppState()
  const { t } = useTranslation()

  const statusTranslation =
    status === 'in_progress'
      ? 'inProgress'
      : status === 'not_started'
      ? 'notStarted'
      : status === 'new'
      ? 'new'
      : 'unfinished'

  const getCTA = (ctaType: CtaType) => {
    if (ctaType === 'remove') {
      return {
        icon: 'TrashCan',
        key: 'remove',
      }
    } else if (ctaType === 'refresh') {
      return {
        icon: 'Refresh',
        key: 'reopen',
      }
    } else if (ctaType === 'start') {
      return {
        icon: 'ArrowRight',
        key: 'start',
      }
    } else if (ctaType === 'continue') {
      return {
        icon: 'Pencil',
        key: 'continue',
      }
    } else {
      return {
        icon: 'Show',
        key: 'show',
      }
    }
  }

  return (
    <Container
      className={className}
      clickable={!!secondCta || (cta !== undefined && !cta.disabled)}
    >
      <Avatars>
        {avatars.map((member, index) => (
          <React.Fragment key={`people-row-avatar-${index}`}>
            {member === 'external' ? (
              <ExternIcon
                size={16}
                color={theme.colors.textLight}
                withBorder
                backgroundSize={40}
              />
            ) : typeof member === 'string' ? (
              <StyledCircleIcon
                type={member}
                size={20}
                color={theme.colors.textLight}
              />
            ) : (
              <StyledAvatar
                user={member}
                big={
                  bigAvatars || index === 0
                    ? type === 'incoming'
                    : type === 'outgoing'
                }
              />
            )}
            {index === 0 &&
              (typeof member !== 'string' || member === 'external') && (
                <StyledIcon
                  type="ArrowRight"
                  color={theme.colors.text72}
                  size={11}
                />
              )}
          </React.Fragment>
        ))}
      </Avatars>
      <Details>
        <NameRow>
          <Text textStyle="labelHeavy">{name}</Text>
          {isNew && <NewDot />}
        </NameRow>
        <DateText textStyle="caption" marginTop={4} color="textLight">
          {date}
        </DateText>
      </Details>
      <Status>
        {overdue && (
          <Overdue
            color="secondaryLight"
            caption={<Icon type="Overdue" color={theme.colors.secondary} />}
          />
        )}
        {elements && elements}
        {status &&
          (status !== 'done' ? (
            <StyledStatusTag
              color="textLightest"
              captionColor="text"
              caption={t(`general.states.${statusTranslation}`)}
              notification={status === 'new' || status === 'unfinished'}
            />
          ) : (
            status === 'done' && (
              <Check>
                <Icon type="Check" color={theme.colors.primary} size={10} />
              </Check>
            )
          ))}
      </Status>
      {cta !== undefined && (
        <CTA>
          {secondCta && (
            <CTAButton
              hoverText={t(`general.actions.${getCTA(secondCta.type).key}`)}
              content={
                <CtaButton
                  icon={getCTA(secondCta.type).icon as IconType}
                  caption={t(`general.actions.${getCTA(secondCta.type).key}`)}
                  colorVariant="secondary"
                  onClick={secondCta.onClick}
                  disabled={secondCta.disabled}
                />
              }
            />
          )}
          <CTAButton
            hoverText={t(`general.actions.${getCTA(cta.type).key}`)}
            content={
              <CtaButton
                icon={getCTA(cta.type).icon as IconType}
                caption={t(`general.actions.${getCTA(cta.type).key}`)}
                colorVariant="secondary"
                onClick={cta.onClick}
                disabled={cta.disabled}
              />
            }
          />
        </CTA>
      )}
    </Container>
  )
}

export default PeopleRow

const CTAButton = styled(JobTag)`
  padding: 0;
  /* Transparent wrapper so only the inner secondary button's fill shows: a
     single primary-15% layer, instead of the JobTag fill doubling up with it. */
  background: transparent;
  /* Mobile: buttons sit side by side and grow to fill the row; when their
     labels don't fit together they wrap to stacked full-width buttons. */
  flex: 1 1 auto;

  ${mqFrom.M`
    flex: initial;
  `}
`

// Mobile shows a labelled, full-width button; desktop keeps the compact
// icon-only button (label hidden) with the JobTag hover tooltip.
const CtaButton = styled(Button)`
  width: 100%;

  label {
    white-space: nowrap;
  }

  ${mqFrom.M`
    width: auto;

    label {
      display: none;
    }

    /* Label is hidden here, so the icon is no longer the last child and keeps
       its 8px right margin — reset it so the icon-only button stays tight. */
    > span {
      margin-right: 0;
    }
  `}
`

const Check = styled.div`
  padding: 4px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.primaryFadeLight};
  border-radius: 50px;
`

const DateText = styled(Text)``

const StyledCircleIcon = styled(CircleIcon)`
  padding: 8px;
`

const CTA = styled(RowAligned)`
  order: 3;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  margin-top: 16px;

  ${mqFrom.M`
    display: none;
    flex-wrap: nowrap;
    width: unset;
    margin: 0 0 0 auto;
  `}
`

const Status = styled(RowAligned)`
  order: 2;
  margin-left: auto;
`

const hoverStyles = (theme: ITheme) => css`
  background-color: ${theme.colors.primaryFadeLight};

  ${DateText} {
    color: ${theme.colors.text};
  }

  ${StyledCircleIcon} {
    background-color: ${theme.colors.lightest};
  }

  ${Status} {
    display: none;
  }

  ${CTA} {
    display: flex;
  }
`

export const Container = styled.div<{
  clickable: boolean
}>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-width: 272px;
  padding: 16px;
  position: relative;
  border-radius: 8px;
  box-shadow: ${shadows.depthTwo};
  background-color: ${({ theme }) => theme.colors.lightest};
  transition: background-color ${instantTransition};

  ${mqTo.M`
    ${Check} {
      display: none;
    }
  `}

  /* Mobile: swap the shadow for a border to match the results blocks. */
  ${mqTo.M`
    border-radius: 12px;
    box-shadow: none;
    ${({ theme }) => `border: 1px solid ${theme.colors.textLighter};`}
  `}

  ${mqFrom.M`
    flex-wrap: nowrap;
    padding: 8px 12px;
    box-shadow: unset;
    min-width: unset;

    ${({ theme, clickable }) =>
      clickable &&
      css`
        &:hover {
          ${hoverStyles(theme)};
        }
      `}

  `}
`

const Avatars = styled.div`
  display: flex;
  align-items: center;
  order: 0;
  margin-right: 12px;

  ${mqFrom.M`
    margin-right: 16px;
  `}
`

const StyledAvatar = styled(Avatar)<{ big: boolean }>`
  width: 40px;
  height: 40px;
  border: 2px solid ${({ theme }) => theme.colors.lightest};
  box-shadow: ${shadows.depthTwo};

  ${mqFrom.M`
    ${({ big }) =>
      !big &&
      `
      width: 28px;
      height: 28px;

    `}
  `}
`

const StyledIcon = styled(Icon)`
  margin: 0 8px;
`

const Details = styled.div`
  order: 1;
`

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Overdue = styled(StatusTag)`
  margin-right: 4px;
  height: 24px;
  align-items: center;
`

const StyledStatusTag = styled(StatusTag)`
  font-weight: normal;
  text-transform: initial;
`
